import { ref } from "vue";
import { logError, logInfo, logMeasurement, FEATURE_NAMES, METRIC_TYPE } from "@/loggers";
import type { Dashboard } from "@/views/ControlTowerView/types";

export enum DashboardRepositionAction {
  Create = "create",
  Delete = "delete",
  Reposition = "reposition"
}

export interface RepositionDashboardsParams {
  action: DashboardRepositionAction;
  dashboards: Dashboard[]; // All dashboards in current state
  targetPosition?: number; // Required for 'create' and 'reposition' - where to place the dashboard
  newDashboardId?: string; // Required for 'create' - ID of newly created dashboard (at last position)
  deleteDashboardId?: string; // Required for 'delete' - ID of dashboard to delete
  repositionDashboardId?: string; // Required for 'reposition' - ID of dashboard being moved
  fromPosition?: number; // Required for 'reposition' - current position of dashboard being moved
}

export interface DashboardUpdateItem {
  dashboard_id: string;
  position: number;
  is_default?: boolean; // Include is_default flag for position 1 updates
}

/**
 * Composable to handle dashboard repositioning logic
 * Provides a simple API for create/delete/reposition operations with automatic position and isDefault management
 *
 * Rules:
 * - Positions are 1-based and continuous (no gaps)
 * - Exactly one dashboard at position 1 has isDefault = true
 * - All others have isDefault = false
 */
export const useDashboardRepositioning = () => {
  const loading = ref(false);

  // --- Helper validation functions ---
  const validateCreateParams = (newDashboardId: string | undefined, targetPosition: number | undefined) => {
    if (!newDashboardId) throw new Error("newDashboardId is required for 'create' action");
    if (targetPosition === undefined || targetPosition < 1)
      throw new Error("targetPosition is required for 'create' action and must be >= 1");
  };

  const validateDeleteParams = (deleteDashboardId: string | undefined, dashboards: Dashboard[]) => {
    if (!deleteDashboardId) throw new Error("deleteDashboardId is required for 'delete' action");
    const dashboardToDelete = dashboards.find((d) => d.id === deleteDashboardId);
    if (dashboardToDelete?.position === 1 || dashboardToDelete?.is_default) {
      logInfo(
        FEATURE_NAMES.MVS_CONTROL_TOWER,
        `Attempted to delete default dashboard (position: ${dashboardToDelete?.position}, isDefault: ${dashboardToDelete?.is_default}). This should be prevented by UI.`
      );
      return false;
    }
    return true;
  };

  const validateRepositionParams = (
    repositionDashboardId: string | undefined,
    fromPosition: number | undefined,
    targetPosition: number | undefined
  ) => {
    if (!repositionDashboardId) throw new Error("repositionDashboardId is required for 'reposition' action");
    if (fromPosition === undefined || fromPosition < 1)
      throw new Error("fromPosition is required for 'reposition' action and must be >= 1");
    if (targetPosition === undefined || targetPosition < 1)
      throw new Error("targetPosition is required for 'reposition' action and must be >= 1");
  };

  /**
   * Main function to compute dashboard position and isDefault updates
   * Returns minimal payload of only dashboards that need updates
   *
   * @param params - Repositioning parameters
   * @returns Array of dashboards with updated positions and isDefault flags
   * @throws Error if validation fails or trying to delete default dashboard
   */
  const repositionDashboards = (params: RepositionDashboardsParams): DashboardUpdateItem[] => {
    const start = performance.now();
    const {
      action,
      dashboards,
      targetPosition,
      newDashboardId,
      deleteDashboardId,
      repositionDashboardId,
      fromPosition
    } = params;

    logInfo(
      FEATURE_NAMES.MVS_CONTROL_TOWER,
      `Repositioning dashboards - Action: ${action}, Total dashboards: ${dashboards.length}`
    );

    let updatedDashboards: DashboardUpdateItem[] = [];

    try {
      switch (action) {
        case "create":
          validateCreateParams(newDashboardId, targetPosition);
          updatedDashboards = handleCreateRepositioning(dashboards, newDashboardId, targetPosition);
          break;
        case "delete":
          if (!validateDeleteParams(deleteDashboardId, dashboards)) return [];
          updatedDashboards = handleDeleteRepositioning(dashboards, deleteDashboardId);
          break;
        case "reposition":
          validateRepositionParams(repositionDashboardId, fromPosition, targetPosition);
          updatedDashboards = handleManageRepositioning(
            dashboards,
            repositionDashboardId,
            fromPosition,
            targetPosition
          );
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }
    } catch (err: any) {
      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Error in repositionDashboards: ${err.message}`);
      throw err;
    }

    logMeasurement(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_LATENCY, performance.now() - start, {
      operation: "repositionDashboards",
      action,
      dashboardsUpdated: updatedDashboards.length
    });

    logInfo(
      FEATURE_NAMES.MVS_CONTROL_TOWER,
      `Repositioning complete - ${updatedDashboards.length} dashboard(s) need updates`
    );

    return updatedDashboards;
  };

  /**
   * Handle repositioning logic for CREATE action
   *
   * Cases:
   * 1. Create at last position (targetPosition === last): No repositioning needed
   * 2. Create at position > 1: Move new dashboard to target, shift others right
   * 3. Create at position 1: Move new dashboard to position 1, shift all right, update isDefault flags
   *
   * @param dashboards - All dashboards including new one at last position
   * @param newDashboardId - ID of newly created dashboard
   * @param targetPosition - Where user wants the new dashboard
   * @returns Dashboards that need updates
   */
  const handleCreateRepositioning = (
    dashboards: Dashboard[],
    newDashboardId: string,
    targetPosition: number
  ): DashboardUpdateItem[] => {
    const updates: DashboardUpdateItem[] = [];
    const sortedDashboards = [...dashboards].sort((a, b) => (a.position || 0) - (b.position || 0));
    const lastPosition = sortedDashboards.length;

    logInfo(
      FEATURE_NAMES.MVS_CONTROL_TOWER,
      `CREATE: Moving new dashboard from position ${lastPosition} to position ${targetPosition}`
    );

    // Case 1: Create at last position - no repositioning needed
    if (targetPosition === lastPosition) {
      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, "Target position equals last position - no repositioning needed");
      return updates;
    }

    // Case 3: Create at position 1 - update isDefault flags
    const isCreatingAtPositionOne = targetPosition === 1;

    // Move new dashboard to target position
    updates.push({
      dashboard_id: newDashboardId,
      position: targetPosition,
      ...(isCreatingAtPositionOne && { is_default: true })
    });

    // Shift dashboards from targetPosition onwards to the right (+1)
    for (let i = 0; i < sortedDashboards.length - 1; i++) {
      const dashboard = sortedDashboards[i];
      const currentPos = dashboard.position || i + 1;

      if (currentPos >= targetPosition) {
        const newPos = currentPos + 1;
        const updateItem: DashboardUpdateItem = {
          dashboard_id: dashboard.id,
          position: newPos
        };

        // If this was the default dashboard at position 1, set is_default = false
        if (isCreatingAtPositionOne && dashboard.is_default) {
          updateItem.is_default = false;
        }

        updates.push(updateItem);
      }
    }

    logInfo(
      FEATURE_NAMES.MVS_CONTROL_TOWER,
      `CREATE: ${updates.length} dashboards need updates${isCreatingAtPositionOne ? " (including isDefault)" : ""}`
    );

    return updates;
  };

  /**
   * Handle repositioning logic for DELETE action
   *
   * Strategy:
   * 1. Move target dashboard to last position
   * 2. Shift dashboards between [targetPos+1...last] left by -1
   * 3. Caller will then delete the last dashboard
   *
   * This ensures:
   * - We always delete from the end
   * - If repositioning fails, delete won't happen
   * - Reduces risk of data inconsistency
   *
   * @param dashboards - All dashboards (target still exists)
   * @param deleteDashboardId - ID of dashboard to delete
   * @returns Dashboards that need position updates before deletion
   */
  const handleDeleteRepositioning = (dashboards: Dashboard[], deleteDashboardId: string): DashboardUpdateItem[] => {
    const updates: DashboardUpdateItem[] = [];
    const sortedDashboards = [...dashboards].sort((a, b) => (a.position || 0) - (b.position || 0));
    const lastPosition = sortedDashboards.length;

    // Find the target dashboard to delete
    const targetDashboard = sortedDashboards.find((d) => d.id === deleteDashboardId);
    if (!targetDashboard) {
      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Dashboard ${deleteDashboardId} not found`);
      throw new Error(`Dashboard ${deleteDashboardId} not found`);
    }

    const deletePosition = targetDashboard.position || 0;

    logInfo(
      FEATURE_NAMES.MVS_CONTROL_TOWER,
      `DELETE: Moving dashboard at position ${deletePosition} to last (${lastPosition}), then will delete`
    );

    // Case 1: Deleting last dashboard - no repositioning needed
    if (deletePosition === lastPosition) {
      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, "Deleting last dashboard - no repositioning needed");
      return updates;
    }

    // Move target dashboard to last position
    updates.push({
      dashboard_id: deleteDashboardId,
      position: lastPosition
    });

    // Shift all dashboards after deletePosition left by -1
    for (const dashboard of sortedDashboards) {
      if (dashboard.id === deleteDashboardId) continue; // Skip the one we're moving

      const currentPos = dashboard.position || 0;
      if (currentPos > deletePosition) {
        updates.push({
          dashboard_id: dashboard.id,
          position: currentPos - 1
        });
      }
    }

    logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `DELETE: ${updates.length} dashboards need repositioning before deletion`);

    return updates;
  };

  /**
   * Handle repositioning logic for REPOSITION (Manage Dashboard) action
   *
   * Cases:
   * 1. Reposition to any position != 1: Standard repositioning logic
   * 2. Reposition involving position 1: Update isDefault flags
   *    - Moving TO position 1: Set new dashboard as default
   *    - Moving FROM position 1: Set new position 1 dashboard as default
   *
   * @param dashboards - All dashboards
   * @param repositionDashboardId - ID of dashboard being moved
   * @param fromPosition - Current position
   * @param targetPosition - Target position
   * @returns Dashboards that need updates
   */
  // --- Helper for shifting dashboards forward ---
  const getForwardShiftUpdates = (
    sortedDashboards: Dashboard[],
    repositionDashboardId: string,
    fromPosition: number,
    targetPosition: number
  ): DashboardUpdateItem[] =>
    sortedDashboards
      .filter(
        (d) => d.id !== repositionDashboardId && (d.position || 0) > fromPosition && (d.position || 0) <= targetPosition
      )
      .map((d) => {
        const newPos = (d.position || 0) - 1;
        return {
          dashboard_id: d.id,
          position: newPos,
          ...(newPos === 1 ? { is_default: true } : {})
        };
      });

  // --- Helper for shifting dashboards backward ---
  const getBackwardShiftUpdates = (
    sortedDashboards: Dashboard[],
    repositionDashboardId: string,
    fromPosition: number,
    targetPosition: number
  ): DashboardUpdateItem[] =>
    sortedDashboards
      .filter(
        (d) => d.id !== repositionDashboardId && (d.position || 0) >= targetPosition && (d.position || 0) < fromPosition
      )
      .map((d) => {
        const currentPos = d.position || 0;
        return {
          dashboard_id: d.id,
          position: currentPos + 1,
          ...(currentPos === 1 ? { is_default: false } : {})
        };
      });

  const handleManageRepositioning = (
    dashboards: Dashboard[],
    repositionDashboardId: string,
    fromPosition: number,
    targetPosition: number
  ): DashboardUpdateItem[] => {
    const updates: DashboardUpdateItem[] = [];
    const sortedDashboards = [...dashboards].sort((a, b) => (a.position || 0) - (b.position || 0));

    logInfo(
      FEATURE_NAMES.MVS_CONTROL_TOWER,
      `REPOSITION: Moving dashboard from position ${fromPosition} to position ${targetPosition}`
    );

    if (fromPosition === targetPosition) {
      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, "Source and target positions are the same - no repositioning needed");
      return updates;
    }

    const isInvolvingPositionOne = fromPosition === 1 || targetPosition === 1;

    // Move the target dashboard to new position
    updates.push({
      dashboard_id: repositionDashboardId,
      position: targetPosition,
      ...(targetPosition === 1 ? { is_default: true } : {})
    });

    // Add updates for other dashboards
    if (fromPosition < targetPosition) {
      updates.push(...getForwardShiftUpdates(sortedDashboards, repositionDashboardId, fromPosition, targetPosition));
    } else {
      updates.push(...getBackwardShiftUpdates(sortedDashboards, repositionDashboardId, fromPosition, targetPosition));
    }

    logInfo(
      FEATURE_NAMES.MVS_CONTROL_TOWER,
      `REPOSITION: ${updates.length} dashboards need updates${isInvolvingPositionOne ? " (including isDefault)" : ""}`
    );

    return updates;
  };

  return {
    loading,
    repositionDashboards
  };
};
